import { useState } from 'react';
import { ArrowLeftOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, Form, Input, Result, Space, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/http/apiClient';
import { useAuth } from '../context/AuthContext';
import { readPendingUserId } from '../storage';

interface TwoFactorFormValues {
  code: string;
}

/** Segundo factor del login: código TOTP de 6 dígitos. */
export function TwoFactorPage() {
  const { verifyTotp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const [pendingUserId] = useState(() => readPendingUserId());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? '/noc';

  if (!pendingUserId) {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <Result
            status="warning"
            title="No hay un inicio de sesión pendiente"
            subTitle="Vuelve a ingresar tu correo y contraseña para solicitar el código 2FA."
            extra={
              <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate('/login', { replace: true })}>
                Volver al login
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const handleSubmit = async (values: TwoFactorFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      await verifyTotp(pendingUserId, values.code);
      message.success('Sesión iniciada.');
      navigate(from, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 429) {
        setError('Demasiados intentos. Espera unos minutos e intenta de nuevo.');
      } else if (caught instanceof ApiError) {
        setError(caught.message);
      } else {
        setError('No se pudo verificar el código. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <SafetyCertificateOutlined style={{ fontSize: 32, color: 'var(--ge-color-accent)' }} />
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            Verificación en dos pasos
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Ingresa el código de 6 dígitos de tu app autenticador.
          </Typography.Paragraph>
        </Space>

        {error && (
          <Alert
            type="error"
            showIcon
            closable
            message={error}
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form<TwoFactorFormValues> layout="vertical" onFinish={handleSubmit} requiredMark={false}>
          <Form.Item
            label="Código"
            name="code"
            rules={[
              { required: true, message: 'Ingresa el código' },
              { len: 6, message: 'El código tiene 6 dígitos' },
            ]}
          >
            <Input.OTP length={6} size="large" autoFocus inputMode="numeric" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
              Verificar
            </Button>
          </Form.Item>
        </Form>

        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/login', { replace: true })}>
          Volver al login
        </Button>
      </Card>
    </div>
  );
}
